/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import type { GridColDef, GridRowIdGetter } from '@mui/x-data-grid';

import SortableTableHead from './SortableTableHead';
import ExpandableRow from './ExpandableRow';
import TablePaginationControls from './TablePaginationControls';
import { AgColor } from '../../Styles/AgColor';

import { useTableControls } from './useTableControls';

export interface CustomTableProps<T = any> {
  rows: T[];
  columns: GridColDef[];
  selectedIds?: (string | number)[];
  setSelectedIDs?: (_values: number[]) => void;
  singleSelect?: boolean;
  disableRowSelectionOnCheck?: boolean;
  disableRowSelectionOnClick?: boolean;
  getRowId?: GridRowIdGetter<any>;
  allowToSelectBoolean?: boolean;
  setSelectedRowIndex?: (_idx: number | undefined) => void;
  stickyHeaderOffset?: number; // used when toolbar is visible
  renderExpandedContent?: (_row: T) => React.ReactNode;
  onPageInfoChange?: (_info: {
    page: number;
    rowsPerPage: number;
    total: number;
  }) => void;
  containerHeight?: number | string;
  disableExpandOn?: (row: T) => boolean;
  slots?: {
    noRowsOverlay?: React.ComponentType;
    [key: string]: any;
  };
}

function defaultGetId(row: any): string | number {
  return typeof row?.id !== 'undefined' ? row.id : JSON.stringify(row);
}

const CustomTable = <T extends any>({
  rows,
  columns,
  selectedIds,
  setSelectedIDs,
  singleSelect,
  disableRowSelectionOnCheck,
  disableRowSelectionOnClick,
  getRowId,
  allowToSelectBoolean,
  setSelectedRowIndex,
  renderExpandedContent,
  onPageInfoChange,
  containerHeight = '73vh',
  disableExpandOn,
  slots,
}: CustomTableProps<T>) => {
  const {
    page,
    rowsPerPage,
    order,
    orderBy,
    expanded,
    toggleExpand,
    handleRequestSort,
    handleChangePage,
    handleChangeRowsPerPage,
    sortedRows,
    pagedRows,
  } = useTableControls<T>({ rows });

  const getId = useCallback(
    (row: any) => (getRowId ? getRowId(row) : defaultGetId(row)),
    [getRowId]
  );

  const pageIds = useMemo(
    () => pagedRows.map((r) => getId(r)),
    [getId, pagedRows]
  );

  const selectable = Boolean(setSelectedIDs);
  const selectedSet = useMemo(
    () => new Set(selectedIds as any[] | undefined),
    [selectedIds]
  );
  const allSelected = selectable && pageIds.every((id) => selectedSet.has(id));
  const someSelected =
    selectable && !allSelected && pageIds.some((id) => selectedSet.has(id));

  const onToggleSelectAll = useCallback(() => {
    if (singleSelect) return; // no select-all in single-select mode
    if (!setSelectedIDs) return;
    if (allSelected) {
      const remaining = (selectedIds || []).filter(
        (id) => !pageIds.includes(id)
      );
      setSelectedIDs(remaining as number[]);
    } else {
      const merged = new Set([...(selectedIds || []), ...pageIds]);
      setSelectedIDs(Array.from(merged) as number[]);
    }
  }, [allSelected, pageIds, selectedIds, setSelectedIDs, singleSelect]);

  const onToggleSelect = useCallback(
    (checked: boolean, id: string | number) => {
      if (!setSelectedIDs) return;
      if (singleSelect) {
        const norm =
          typeof id === 'string' && /^\d+$/.test(id)
            ? parseInt(id, 10)
            : (id as any);
        const next = checked ? [norm] : [];
        setSelectedIDs(next as number[]);
        return;
      }
      const current = new Set(selectedIds as any[] | undefined);
      if (checked) current.add(id);
      else current.delete(id);
      // prefer numeric ids if possible
      const next = Array.from(current).map((x) =>
        typeof x === 'string' && /^\d+$/.test(x) ? parseInt(x, 10) : (x as any)
      );
      setSelectedIDs(next as number[]);
    },
    [selectedIds, setSelectedIDs, singleSelect]
  );

  const onRowClick = useCallback(
    (row: any) => {
      if (!setSelectedRowIndex) return;
      const rid = getId(row);
      if (typeof rid === 'number') {
        setSelectedRowIndex(rid - 1);
        return;
      }
      if (typeof rid === 'string' && /^\d+$/.test(rid)) {
        setSelectedRowIndex(parseInt(rid, 10) - 1);
        return;
      }
      // fallback to index in original rows
      const idx = rows.findIndex((r) => getId(r) === rid);
      setSelectedRowIndex(idx >= 0 ? idx : undefined);
    },
    [getId, rows, setSelectedRowIndex]
  );

  useEffect(() => {
    onPageInfoChange &&
      onPageInfoChange({ page, rowsPerPage, total: sortedRows.length });
  }, [onPageInfoChange, page, rowsPerPage, sortedRows.length]);

  const NoRowsOverlay = slots?.noRowsOverlay;

  return (
    <Paper
      elevation={0}
      sx={{ width: '100%', overflow: 'hidden', border: 0, borderRadius: 1 }}
    >
      <TableContainer sx={{ height: containerHeight, overflowY: 'auto' }}>
        <Table
          stickyHeader
          aria-label='custom table'
          size='small'
          sx={{
            tableLayout: 'fixed',
            '& .MuiTableCell-stickyHeader': {
              backgroundColor: AgColor.grey0,
              top: 0,
              zIndex: 1,
            },
          }}
        >
          <SortableTableHead
            columns={columns}
            order={order}
            orderBy={orderBy as string}
            onRequestSort={handleRequestSort}
            selectable={selectable}
            singleSelect={Boolean(singleSelect)}
            allSelected={allSelected}
            someSelected={someSelected}
            onToggleSelectAll={onToggleSelectAll}
            showExpand={Boolean(renderExpandedContent)}
          />
          <TableBody>
            {pagedRows.length === 0 ? (
              NoRowsOverlay ? (
                <TableRow>
                  <TableCell
                    sx={{ border: 0 }}
                    colSpan={
                      columns.length +
                      (selectable ? 1 : 0) +
                      (renderExpandedContent ? 1 : 0)
                    }
                  >
                    <NoRowsOverlay />
                  </TableCell>
                </TableRow>
              ) : null
            ) : (
              pagedRows.map((row: any) => (
                <ExpandableRow
                  disableExpandOn={disableExpandOn}
                  key={String(getId(row))}
                  row={row}
                  columns={columns}
                  selectable={selectable}
                  selected={selectedSet.has(getId(row))}
                  onToggleSelect={onToggleSelect}
                  disableRowSelectionOnCheck={disableRowSelectionOnCheck}
                  disableRowSelectionOnClick={disableRowSelectionOnClick}
                  isExpanded={expanded.has(getId(row))}
                  onToggleExpand={toggleExpand}
                  getId={getId}
                  allowToSelectBoolean={allowToSelectBoolean}
                  renderExpandedContent={renderExpandedContent as any}
                  onRowClick={onRowClick}
                  showExpand={Boolean(renderExpandedContent)}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationControls
        count={sortedRows.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default CustomTable;
